import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { personalInfo } from '../data/portfolio';
import {
  Menu,
  X,
  Github,
  Linkedin,
  Mail,
  Sun,
  Moon,
  Monitor,
  Download,
  ChevronDown,
} from 'lucide-react';

import { SecretMenuContext } from './SecretMobileMenu';

const navLinks = [
  { label: 'About',      href: '#about'      },
  { label: 'Skills',     href: '#skills'     },
  { label: 'Projects',   href: '#projects'   },
  { label: 'Apps',       href: '#apps'       },
  { label: 'Experience', href: '#experience' },
  { label: 'Life',       href: '#life'       },
  { label: 'Contact',    href: '#contact'    },
];

const themeOptions = [
  { value: 'light',  label: 'Light',  icon: Sun     },
  { value: 'dark',   label: 'Dark',   icon: Moon    },
  { value: 'system', label: 'System', icon: Monitor },
];

/* ── Theme toggle ─────────────────────────────────────────────────────────── */
const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen]     = useState(false);
  const ref                  = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current     = themeOptions.find(t => t.value === theme) || themeOptions[1];
  const CurrentIcon = current.icon;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200 text-sm"
        aria-label="Toggle theme"
      >
        <CurrentIcon size={14} />
        <span className="hidden sm:inline text-xs">{current.label}</span>
        <ChevronDown
          size={12}
          className="transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{    opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-36 glass rounded-xl border border-border/60 shadow-lg overflow-hidden z-50"
          >
            {themeOptions.map((opt) => {
              const Icon     = opt.icon;
              const isActive = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => { setTheme(opt.value); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150"
                  style={{
                    color:      isActive ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                    background: isActive ? 'hsl(var(--primary) / 0.08)' : 'transparent',
                  }}
                  onMouseEnter={e => !isActive && (e.currentTarget.style.color = 'hsl(var(--foreground))')}
                  onMouseLeave={e => !isActive && (e.currentTarget.style.color = 'hsl(var(--muted-foreground))')}
                >
                  <Icon size={14} />
                  <span>{opt.label}</span>
                  {isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full"
                      style={{ background: 'hsl(var(--primary))' }} />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Secret logo tap — lives at the bottom of the open mobile menu ────────── *
 *                                                                              *
 *  Looks exactly like a faint decorative "made by" watermark.                 *
 *  3 taps within 700ms fires onTrigger.                                       *
 *  Each tap gives a subtle scale-down pulse so the user feels progress        *
 *  without any text or visual hint revealing the purpose.                     *
 * ─────────────────────────────────────────────────────────────────────────── */
const SecretLogoTap = ({ onTrigger }) => {
  const tapCount = useRef(0);
  const tapTimer = useRef(null);
  const [pulse,  setPulse]  = useState(false);

  const handleTap = (e) => {
    e.stopPropagation();

    tapCount.current += 1;

    // Tactile-feeling scale pulse on each tap
    setPulse(true);
    setTimeout(() => setPulse(false), 160);

    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 700);

    if (tapCount.current >= 3) {
      tapCount.current = 0;
      clearTimeout(tapTimer.current);
      onTrigger();
    }
  };

  return (
    <div
      aria-hidden="true"
      onTouchEnd={handleTap}
      onClick={handleTap}
      style={{ cursor: 'default', userSelect: 'none', WebkitUserSelect: 'none' }}
      className="flex items-center justify-center py-3"
    >
      <motion.div
        animate={{ scale: pulse ? 0.82 : 1, opacity: pulse ? 0.35 : 0.18 }}
        transition={{ duration: 0.14, ease: 'easeOut' }}
        className="flex items-center gap-1.5"
      >
        <img
          src="logo/logo.png"
          style={{ width: 12, height: 12 }}
          alt=""
          draggable={false}
        />
        <span style={{
          fontSize: 10,
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 500,
          letterSpacing: '-0.01em',
          color: 'hsl(var(--muted-foreground))',
        }}>
          rajibr<span style={{ color: 'hsl(var(--primary))' }}>.com</span>
        </span>
      </motion.div>
    </div>
  );
};

/* ── Navbar ───────────────────────────────────────────────────────────────── */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // triggerSecretMenu exposed by SecretMenuProvider
  const { triggerSecretMenu } = useContext(SecretMenuContext);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0,   opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass border-b border-border/40 shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">

          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-2 group"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <img src="logo/logo.png" style={{ width: 30, height: 30 }} alt="Rajib Logo" />
            <span className="font-heading font-semibold text-foreground text-sm tracking-tight">
              rajibr<span className="gradient-text">.com</span>
            </span>
          </motion.a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link, i) => (
              <motion.button
                key={link.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1,  y: 0   }}
                transition={{ delay: 0.1 * i, duration: 0.4 }}
                onClick={() => handleNavClick(link.href)}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-primary rounded-lg transition-colors duration-200 hover:bg-muted/40"
              >
                {link.label}
              </motion.button>
            ))}
          </div>

          {/* Desktop right controls */}
          <div className="hidden md:flex items-center gap-3">
            <a href="https://github.com/bboyrajib" target="_blank" rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors duration-200">
              <Github size={18} />
            </a>
            <a href="https://linkedin.com/in/rajib-roy" target="_blank" rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors duration-200">
              <Linkedin size={18} />
            </a>

            <ThemeToggle />

            <Button
              variant="outline" size="sm"
              className="border-primary/30 text-primary hover:bg-primary/10 hover:text-foreground hover:border-primary/60 font-medium"
              asChild
            >
              <a href={personalInfo.resume} target="_blank" rel="noopener noreferrer">
                <Download size={13} className="mr-1.5" />
                Resume
              </a>
            </Button>

            <Button
              size="sm"
              className="font-medium bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => handleNavClick('#contact')}
            >
              Hire Me
            </Button>
          </div>

          {/* Mobile: theme + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className="text-foreground p-2 rounded-lg hover:bg-muted/40 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* ── Mobile menu ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0   }}
            exit={{    opacity: 0, y: -20  }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-16 z-40 glass border-b border-border/40 md:hidden"
          >
            <div className="px-6 pt-5 pb-2 flex flex-col gap-2">

              {/* Nav links */}
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded-lg transition-colors"
                >
                  {link.label}
                </button>
              ))}

              {/* Social / Resume row */}
              <div className="flex items-center gap-4 pt-4 border-t border-border/40 flex-wrap">
                <a href="https://github.com/bboyrajib" target="_blank" rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors">
                  <Github size={18} />
                </a>
                <a href="https://linkedin.com/in/rajib-roy" target="_blank" rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors">
                  <Linkedin size={18} />
                </a>
                <a href={`mailto:${personalInfo.email}`}
                  className="text-muted-foreground hover:text-foreground transition-colors">
                  <Mail size={18} />
                </a>
                <a href={personalInfo.resume} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors font-medium">
                  <Download size={14} />
                  Resume
                </a>
              </div>

              {/* ── Secret tap zone ────────────────────────────────────────── *
               *  Appears as faint "rajibr.com" watermark branding at the      *
               *  bottom of the menu. 3 taps → secret menu opens.              *
               *  Closes the hamburger menu first, then fires the drawer.      *
               * ────────────────────────────────────────────────────────────── */}
              <SecretLogoTap
                onTrigger={() => {
                  setMenuOpen(false);
                  // Small delay so the menu close animation plays first
                  setTimeout(triggerSecretMenu, 180);
                }}
              />

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;